import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Waitlist Form Component
const WaitlistForm = ({ onSuccess, buttonText = "Join the Waitlist", className = "" }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [showOptionalFields, setShowOptionalFields] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setMessage({ type: 'error', text: 'Please enter your full name' });
      return;
    }
    if (!email.trim()) {
      setMessage({ type: 'error', text: 'Please enter your email address' });
      return;
    }
    
    setIsLoading(true);
    setMessage(null);
    
    try {
      const res = await axios.post(`${API}/waitlist/join`, { 
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null
      });
      if (res.data.success) {
        setMessage({ type: 'success', text: res.data.message });
        setName('');
        setEmail('');
        setPhone('');
        setShowOptionalFields(false);
        if (onSuccess) onSuccess();
      } else {
        setMessage({ type: 'error', text: res.data.error || 'Something went wrong' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to join waitlist. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={`w-full ${className}`}>
      <div className="space-y-3">
        {/* Name - Required */}
        <input
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (e.target.value && !showOptionalFields) setShowOptionalFields(true);
          }}
          placeholder="Full Name *"
          required
          className="w-full px-5 py-4 rounded-xl bg-slate-700 border border-slate-500 text-white placeholder-slate-400 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all [&]:!text-white"
        />
        
        {/* Email & Phone Fields */}
        <AnimatePresence>
          {showOptionalFields && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-3"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email *"
                required
                className="w-full px-5 py-4 rounded-xl bg-slate-700 border border-slate-500 text-white placeholder-slate-400 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all [&]:!text-white"
              />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone (optional)"
                className="w-full px-5 py-4 rounded-xl bg-slate-700 border border-slate-500 text-white placeholder-slate-400 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all [&]:!text-white"
              />
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full px-8 py-4 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white text-lg font-semibold hover:opacity-90 transition-all shadow-xl shadow-purple-500/25 hover:shadow-purple-500/40 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Joining...
            </span>
          ) : buttonText}
        </button>
      </div>
      
      <AnimatePresence>
        {message && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`mt-3 text-sm text-center ${message.type === 'success' ? 'text-emerald-400' : 'text-red-400'}`}
          >
            {message.text}
          </motion.p>
        )}
      </AnimatePresence>
    </form>
  );
};

// Logo component with GeniusList branding
const Logo = ({ className = "", showText = true }) => {
  return (
    <motion.div
      className={`flex items-center gap-2 ${className}`}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <img 
        src="/logo.png" 
        alt="GeniusList" 
        className="h-10 w-auto"
      />
      {showText && (
        <span className="text-xl font-bold text-white tracking-tight">
          Genius<span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">List</span>
        </span>
      )}
    </motion.div>
  );
};

// Navbar Component
const Navbar = ({ onGetStarted, onSignIn }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { label: "Features", href: "#features" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "Pricing", href: "#pricing" },
    { label: "FAQ", href: "#faq" },
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? "bg-slate-900/80 backdrop-blur-lg border-b border-slate-700/50" 
            : "bg-transparent"
        }`}
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            <Logo />

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className="text-sm font-medium text-slate-300 hover:text-white transition-colors"
                >
                  {item.label}
                </a>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-4">
              <button
                onClick={onSignIn}
                className="text-sm font-medium text-slate-300 hover:text-white transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={scrollToTop}
                className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm font-semibold hover:opacity-90 transition-opacity shadow-lg shadow-purple-500/25"
              >
                Join Waitlist
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-slate-300 hover:text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-0 z-40 lg:hidden bg-slate-900/95 backdrop-blur-lg"
            style={{ paddingTop: 'calc(5rem + env(safe-area-inset-top, 0px))' }}
          >
            <div className="flex flex-col items-center gap-6 p-8">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-lg font-medium text-slate-300 hover:text-white transition-colors"
                >
                  {item.label}
                </a>
              ))}
              <button
                onClick={() => { setIsMobileMenuOpen(false); onSignIn(); }}
                className="text-lg font-medium text-slate-300 hover:text-white transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => { setIsMobileMenuOpen(false); scrollToTop(); }}
                className="mt-4 w-full max-w-xs px-6 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold"
              >
                Join Waitlist
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Hero Section
const Hero = ({ onGetStarted }) => {
  return (
    <section className="min-h-screen pb-20 relative overflow-hidden" style={{ paddingTop: 'calc(5rem + env(safe-area-inset-top, 0px))' }}>
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-purple-950/20 to-slate-950" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-4xl mx-auto">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 mb-8"
          >
            <span className="text-purple-400">🚀</span>
            <span className="text-sm font-medium text-purple-300">Launching Soon</span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 leading-tight"
          >
            Create Stunning{" "}
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
              Real Estate Marketing
            </span>{" "}
            in Minutes
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg sm:text-xl text-slate-400 mb-10 max-w-2xl mx-auto"
          >
            Generate professional listing descriptions, social media posts, feature sheets, 
            and more with AI. Save hours on every listing.
          </motion.p>

          {/* Waitlist Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="max-w-xl mx-auto"
          >
            <WaitlistForm buttonText="Join the Waitlist →" />
          </motion.div>

          {/* Trust Badge */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mt-8 text-sm text-slate-500"
          >
            ✓ Be first to get access · ✓ Free to join · ✓ No spam, ever
          </motion.p>
        </div>

        {/* Feature Showcase - AI Generated Content Examples */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16 relative"
        >
          <div className="grid md:grid-cols-3 gap-6">
            {/* AI Listing Description */}
            <div className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                  <span className="text-white text-lg">📝</span>
                </div>
                <div>
                  <h4 className="font-semibold text-white text-sm">AI Listing Description</h4>
                  <p className="text-xs text-slate-400">Generated in 5 seconds</p>
                </div>
              </div>
              <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/30">
                <p className="text-slate-300 text-sm leading-relaxed">
                  "Welcome to this stunning 4-bedroom executive home in sought-after Maple Ridge. 
                  Features include soaring 10ft ceilings, chef's kitchen with quartz counters, 
                  and a private backyard oasis..."
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <span className="px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs">MLS Ready</span>
                  <span className="px-2 py-1 rounded-full bg-purple-500/20 text-purple-400 text-xs">SEO Optimized</span>
                </div>
              </div>
            </div>

            {/* Social Media Post */}
            <div className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center">
                  <span className="text-white text-lg">📸</span>
                </div>
                <div>
                  <h4 className="font-semibold text-white text-sm">Instagram Caption</h4>
                  <p className="text-xs text-slate-400">Engagement optimized</p>
                </div>
              </div>
              <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/30">
                <p className="text-slate-300 text-sm leading-relaxed">
                  "✨ JUST LISTED ✨<br/><br/>
                  This is the one you've been waiting for! 🏡<br/><br/>
                  📍 123 Maple Avenue<br/>
                  🛏️ 4 Beds | 🛁 3 Baths<br/>
                  💰 $899,000"
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <span className="px-2 py-1 rounded-full bg-pink-500/20 text-pink-400 text-xs">#JustListed</span>
                  <span className="px-2 py-1 rounded-full bg-blue-500/20 text-blue-400 text-xs">#DreamHome</span>
                </div>
              </div>
            </div>

            {/* Feature Sheet */}
            <div className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                  <span className="text-white text-lg">📄</span>
                </div>
                <div>
                  <h4 className="font-semibold text-white text-sm">Feature Sheet PDF</h4>
                  <p className="text-xs text-slate-400">Print-ready design</p>
                </div>
              </div>
              <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/30">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Property Type</span>
                    <span className="text-white">Detached Home</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Square Feet</span>
                    <span className="text-white">2,450 sq ft</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Year Built</span>
                    <span className="text-white">2019</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Lot Size</span>
                    <span className="text-white">0.25 acres</span>
                  </div>
                </div>
                <div className="mt-3">
                  <span className="px-2 py-1 rounded-full bg-cyan-500/20 text-cyan-400 text-xs">Download PDF</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Gradient Overlay */}
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-slate-950 to-transparent pointer-events-none" />
        </motion.div>
      </div>
    </section>
  );
};

// Problem Section
const ProblemSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const painPoints = [
    { icon: "✏️", title: "Rewriting MLS Descriptions", description: "Hours spent crafting listing copy for every new property." },
    { icon: "📱", title: "Social Media Overload", description: "Struggling to keep up with consistent posting." },
    { icon: "🎨", title: "Inconsistent Branding", description: "Marketing materials that don't match your image." },
    { icon: "⏰", title: "Wasted Time on Design", description: "Hours lost instead of building client relationships." },
  ];

  return (
    <section className="py-20 lg:py-32 bg-slate-950 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-radial from-purple-600/5 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              From Hours of Manual Work to{" "}
              <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                Minutes of AI Automation
              </span>
            </h2>
            <p className="text-lg text-slate-400 mb-4">
              Real estate agents spend an average of 3+ hours per listing on marketing 
              materials alone. That's time stolen from what matters most — your clients.
            </p>
            <p className="text-lg text-slate-400">
              GeniusList transforms this entire process, letting you create professional, 
              consistent marketing assets in just minutes.
            </p>
          </motion.div>

          <div className="space-y-4">
            {painPoints.map((point, index) => (
              <motion.div
                key={point.title}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 p-6 rounded-2xl hover:border-slate-600/50 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center shrink-0 text-2xl">
                    {point.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">{point.title}</h4>
                    <p className="text-sm text-slate-400">{point.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Features Section
const FeaturesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const tools = [
    { icon: "📝", title: "AI Listing Generator", description: "Professional MLS-ready descriptions" },
    { icon: "📸", title: "Instagram Posts", description: "Scroll-stopping visual captions" },
    { icon: "👥", title: "Facebook Posts", description: "Engaging community content" },
    { icon: "💼", title: "LinkedIn Posts", description: "Professional network reach" },
    { icon: "🎬", title: "Video Scripts", description: "Walk-through narratives" },
    { icon: "📧", title: "Email Templates", description: "Nurture leads effortlessly" },
    { icon: "📄", title: "Feature Sheets", description: "Print-ready PDFs" },
    { icon: "🛋️", title: "Virtual Staging", description: "AI-powered staging" },
    { icon: "#️⃣", title: "Hashtag Generator", description: "Maximize visibility" },
    { icon: "🌍", title: "Multi-Language", description: "Reach broader markets" },
  ];

  return (
    <section id="features" className="py-20 lg:py-32 bg-slate-900 relative overflow-hidden">
      <div className="absolute top-1/4 left-0 w-1/3 h-1/2 bg-gradient-radial from-purple-600/5 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 mb-4">
            <span className="text-sm font-medium text-purple-300">17+ AI Tools</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Your Entire Marketing Stack,{" "}
            <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              Powered by AI
            </span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Specialized tools designed specifically for real estate professionals. 
            From social media to client emails, we've got you covered.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {tools.map((tool, index) => (
            <motion.div
              key={tool.title}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 p-5 rounded-xl hover:border-purple-500/30 hover:bg-slate-800/70 transition-all cursor-pointer group"
            >
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center mb-3 text-xl group-hover:scale-110 transition-transform">
                {tool.icon}
              </div>
              <h4 className="font-semibold text-white mb-1 text-sm">{tool.title}</h4>
              <p className="text-xs text-slate-400">{tool.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorks = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const steps = [
    { number: "01", icon: "📋", title: "Tell us about the property", description: "Fill in our guided smart form with property details and upload your best photos." },
    { number: "02", icon: "✨", title: "Choose your marketing tools", description: "Select from 17+ AI-powered tools — MLS descriptions, social posts, feature sheets, and more." },
    { number: "03", icon: "🚀", title: "Publish everywhere in minutes", description: "Copy to MLS, share on social media, download PDFs. Your complete marketing kit, ready to go." },
  ];

  return (
    <section id="how-it-works" className="py-20 lg:py-32 bg-slate-950 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            How It <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Works</span>
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            From property details to a complete marketing kit in three simple steps.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="relative"
            >
              <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 h-full hover:border-slate-600/50 transition-colors">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-500 mb-6">
                  <span className="text-2xl font-bold text-white">{step.number}</span>
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mb-4 text-2xl">
                  {step.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                <p className="text-slate-400">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = ({ onGetStarted }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const plans = [
    {
      name: "Free",
      price: "$0",
      period: "forever",
      description: "Perfect for trying GeniusList",
      features: ["5 listings per month", "Basic AI descriptions", "Limited tool access", "Community support"],
      cta: "Join Waitlist",
      popular: false,
    },
    {
      name: "Basic",
      price: "$29.99",
      period: "/month",
      description: "For active individual agents",
      features: ["25 listings per month", "Social media posts", "Email templates", "Basic support"],
      cta: "Join Waitlist",
      popular: false,
    },
    {
      name: "Premium",
      price: "$59.99",
      period: "/month",
      description: "For power users who want it all",
      features: ["Unlimited listings", "All 17+ AI tools", "Feature sheets & PDFs", "Virtual staging", "Priority support"],
      cta: "Join Waitlist",
      popular: true,
    },
    {
      name: "Brokerage",
      price: "$199",
      period: "/month",
      description: "For teams and brokerages",
      features: ["Up to 25 team accounts", "White-label PDFs", "API access", "Custom branding", "Dedicated support"],
      cta: "Contact Us",
      popular: false,
    },
  ];

  return (
    <section id="pricing" className="py-20 lg:py-32 bg-slate-900 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-radial from-purple-600/5 via-transparent to-transparent" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Simple, Transparent <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Pricing</span>
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Choose the plan that fits your needs. All plans include our core AI features.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`relative bg-slate-800/50 backdrop-blur-sm border rounded-2xl p-6 ${
                plan.popular ? "border-purple-500 ring-1 ring-purple-500" : "border-slate-700/50"
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <div className="px-4 py-1 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 text-xs font-semibold text-white">
                    Most Popular
                  </div>
                </div>
              )}

              <h3 className="text-xl font-bold text-white mb-1">{plan.name}</h3>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-3xl font-bold text-white">{plan.price}</span>
                <span className="text-slate-400 text-sm">{plan.period}</span>
              </div>
              <p className="text-sm text-slate-400 mb-6">{plan.description}</p>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm text-white">
                    <span className="text-emerald-400 shrink-0">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                onClick={scrollToTop}
                className={`w-full py-3 rounded-lg font-semibold transition-all ${
                  plan.popular
                    ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:opacity-90"
                    : "border border-slate-600 text-white hover:bg-slate-700/50"
                }`}
              >
                {plan.cta}
              </button>
            </motion.div>
          ))}
        </div>
        
        <p className="text-center text-sm text-slate-500 mt-8">
          All paid plans • Cancel anytime
        </p>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    { question: "Do I need to be tech-savvy to use it?", answer: "Not at all! GeniusList is designed for real estate professionals, not tech experts. If you can fill out a form and click a button, you can use GeniusList." },
    { question: "Is my listing and client data secure?", answer: "Absolutely. We use enterprise-grade encryption, never share your data with third parties, and comply with all major data protection regulations." },
    { question: "Will this work with my MLS?", answer: "Yes! GeniusList generates content that you can copy directly into any MLS system. We also provide download options for PDFs and easy sharing for social media." },
    { question: "Is there a free plan?", answer: "Yes, we offer a free tier that lets you create up to 5 listings per month with basic AI descriptions. Perfect for trying out the platform." },
    { question: "Can I customize the AI outputs?", answer: "Absolutely! Every piece of content can be customized. You can choose different tones (professional, luxury, investor-friendly) and edit the generated text." },
  ];

  return (
    <section id="faq" className="py-20 lg:py-32 bg-slate-950 relative overflow-hidden">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Frequently Asked <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Questions</span>
          </h2>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-4 text-left flex items-center justify-between text-white font-medium hover:bg-slate-700/30 transition-colors"
              >
                {faq.question}
                <span className={`transform transition-transform ${openIndex === index ? "rotate-180" : ""}`}>
                  ▼
                </span>
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <p className="px-6 pb-4 text-slate-400">{faq.answer}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = ({ onGetStarted }) => {
  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/30 via-slate-950 to-blue-900/30" />
      <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/20 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />

      <div className="max-w-4xl mx-auto px-4 relative z-10 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/20 border border-purple-500/30 mb-6">
            <span className="text-sm font-medium text-purple-300">🚀 Be First in Line</span>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Ready to Transform Your{" "}
            <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              Real Estate Marketing?
            </span>
          </h2>

          <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
            Join thousands of real estate professionals on our waitlist. Be the first to experience the future of listing marketing.
          </p>

          <div className="max-w-xl mx-auto">
            <WaitlistForm buttonText="Join the Waitlist →" />
          </div>

          <p className="mt-6 text-sm text-slate-500">
            🔒 We respect your privacy · No spam, ever
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-16 bg-slate-950 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-8">
          <div>
            <Logo />
            <p className="text-slate-500 mt-2 text-sm">AI-powered real estate marketing</p>
          </div>
          <div className="flex gap-6">
            <a href="#" className="text-sm text-slate-400 hover:text-white transition-colors">Privacy Policy</a>
            <a href="#" className="text-sm text-slate-400 hover:text-white transition-colors">Terms of Service</a>
            <a href="#" className="text-sm text-slate-400 hover:text-white transition-colors">Contact</a>
          </div>
        </div>
        <div className="mt-8 pt-8 border-t border-slate-800 text-center">
          <p className="text-sm text-slate-500">© {new Date().getFullYear()} GeniusList. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page Component
const LandingPage = ({ onGetStarted, onSignIn, referralCode, referralPartnerName }) => {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Referral Banner */}
      {referralPartnerName && (
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-2 px-4 text-center">
          <div className="flex items-center justify-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="8" width="18" height="4" rx="1"/>
              <path d="M12 8v13"/>
              <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/>
              <path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 4.8 0 0 1 12 8a4.8 4.8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"/>
            </svg>
            <span className="text-sm font-medium">
              You were referred by <span className="font-bold">{referralPartnerName}</span> — Welcome to GeniusList!
            </span>
          </div>
        </div>
      )}
      <Navbar onGetStarted={onGetStarted} onSignIn={onSignIn} />
      <Hero onGetStarted={onGetStarted} />
      <ProblemSection />
      <FeaturesSection />
      <HowItWorks />
      <PricingSection onGetStarted={onGetStarted} />
      <FAQSection />
      <CTASection onGetStarted={onGetStarted} />
      <Footer />
    </div>
  );
};

export default LandingPage;
